import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { ButtonLoader } from "Components";
import { Validator } from "Utils/validator";
import { newAuthService } from "Service/Axios";
import { saveUserProfileUrl } from "Constant/api";
import {
  DEFAULT_MODULE_TYPE,
  DOCUMENT_TYPE_PDF_ONLY,
  VIEW_PROFILE_SECTIONS,
} from "Constant/constants";
import { uploadHandler } from "Utils";

const PublicationTypes = [
  "Journal Article",
  "Conference Paper",
  "Book Chapter",
  "Book",
  "Technical Report",
  "Preprint",
  "Other",
];

export const PublicationModal = ({
  show,
  publicationData,
  toggle,
  fetchData,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  const [formData, setFormData] = useState({
    gid: "",
    titleOfThePublication: "",
    publicationType: "",
    otherPublicationType: "",
    journalOrConference: "",
    authors: [""],
    isFirstAuthor: "",
    yearOfPublication: "",
    doiOrUlr: "",
    abstract: "",
    publisherName: "",
    keywordsOrResearcTopics: "",
    publicationOpenAccess: "",
    uploadPdf: null,
  });
  const [errMsg, setErrMsg] = useState({
    titleOfThePublication: "",
    publicationType: "",
    isFirstAuthor: "",
    yearOfPublication: "",
    publisherName: "",
    publicationOpenAccess: "",
  });

  const {
    gid,
    titleOfThePublication,
    publicationType,
    otherPublicationType,
    journalOrConference,
    authors,
    isFirstAuthor,
    yearOfPublication,
    doiOrUlr,
    abstract,
    publisherName,
    keywordsOrResearcTopics,
    publicationOpenAccess,
    uploadPdf,
  } = formData;

  const validationRequiredFields = [
    "titleOfThePublication",
    "publicationType",
    "isFirstAuthor",
    "yearOfPublication",
    "publisherName",
    "publicationOpenAccess",
  ];

  const convertBooleanToString = (val) => {
    if (val === true) {
      return "yes";
    }
    if (val === false) {
      return "no";
    }
    return "";
  };

  const convertStringToBoolean = (val) => {
    if (val === "yes") {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (publicationData) {
      setFormData({
        gid: publicationData.gid || "",
        titleOfThePublication: publicationData.title || "",
        publicationType: publicationData.type || "",
        otherPublicationType: publicationData.otherPublicationType || "",
        journalOrConference: publicationData.journalOrConference || "",
        authors: publicationData.authors || [""],
        isFirstAuthor: convertBooleanToString(publicationData.firstAuthor),
        yearOfPublication: publicationData.yearOfPublication || "",
        doiOrUlr: publicationData.doiOrUlr || "",
        abstract: publicationData.abstractDtls || "",
        publisherName: publicationData.publisherName || "",
        keywordsOrResearcTopics: publicationData.keywordsOrResearcTopics || "",
        publicationOpenAccess: convertBooleanToString(
          publicationData.publicationOpenAccess
        ),
        uploadPdf: publicationData.uploadPdf || null,
      });
    }
  }, [publicationData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationRequiredFields.includes(name)) {
      if (!Validator.text(value)) {
        setErrMsg((prev) => ({
          ...prev,
          [name]: t(
            `common.messages.error.viewProfile.publication.${name}.blank`
          ),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    }
  };

  const handleAuthorChange = (idx, value) => {
    setFormData((prev) => {
      const authors = [...prev.authors];
      authors[idx] = value;
      return { ...prev, authors };
    });
  };

  const handleAuthorAdd = () => {
    setFormData((prev) => ({ ...prev, authors: [...prev.authors, ""] }));
  };

  const handleAuthorRemove = (idx) => {
    setFormData((prev) => {
      const authors = prev.authors.filter((_, i) => i !== idx);
      return { ...prev, authors };
    });
  };

  const handleFileChange = async (e) => {
    setUploadLoading(true);
    const response = await uploadHandler(
      e,
      DEFAULT_MODULE_TYPE.userDocuments,
      DOCUMENT_TYPE_PDF_ONLY
    );
    if (response) {
      setFormData((prev) => ({
        ...prev,
        uploadPdf: response,
      }));
      setUploadLoading(false);
    } else {
      setUploadLoading(false);
    }
  };

  const handleFileRemove = () => {
    setFormData((prev) => ({
      ...prev,
      uploadPdf: null,
    }));
  };

  const getValidation = () => {
    return new Promise((resolve) => {
      let isValid = true;
      const errors = {};

      // Validate always-required fields
      for (const field of validationRequiredFields) {
        const value = formData[field];
        if (!Validator.text(value)) {
          errors[field] = t(
            `common.messages.error.viewProfile.publication.${field}.blank`
          );
          isValid = false;
        }
      }

      // Set error messages
      setErrMsg((prev) => ({
        ...prev,
        ...errors,
      }));

      resolve(isValid);
    });
  };

  const onSavePublication = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      try {
        const payload = {
          key: VIEW_PROFILE_SECTIONS.publication,
          [VIEW_PROFILE_SECTIONS.publication]: {
            title: titleOfThePublication,
            type: publicationType,
            otherPublicationType,
            journalOrConference,
            authors,
            firstAuthor: convertStringToBoolean(isFirstAuthor),
            yearOfPublication,
            doiOrUlr,
            abstractDtls: abstract,
            publisherName,
            keywordsOrResearcTopics,
            publicationOpenAccess: convertStringToBoolean(
              publicationOpenAccess
            ),
            uploadPdf,
          },
        };

        if (gid.trim()) {
          payload[VIEW_PROFILE_SECTIONS.publication].gid = gid;
        }

        setLoading(true);
        const response = await newAuthService(
          saveUserProfileUrl,
          "POST",
          payload
        );
        if (response.status) {
          toast.success(
            t("common.messages.notification.success.profileUpdated")
          );
          fetchData();
          toggle();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error(
          t("common.messages.notification.failed.somethingWentWrong")
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal size="lg" show={show} onHide={toggle} className="highlightsModal">
      <Modal.Header closeButton>
        <Modal.Title className="text-md font-medium">
          {t(`auth.viewProfile.${gid ? "editPublication" : "addPublication"}`)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.titleOfThePublication")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="titleOfThePublication"
              placeHolder={t("common.placeHolder.titleOfThePublication")}
              value={titleOfThePublication}
              onChange={handleChange}
            />
            {errMsg.titleOfThePublication && (
              <p className="error">{errMsg.titleOfThePublication}</p>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.publicationType")}{" "}
              <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              name="publicationType"
              value={publicationType}
              onChange={handleChange}
            >
              <option value="">
                {t("common.placeHolder.publicationType")}
              </option>
              {PublicationTypes.map((pubType) => (
                <option value={pubType} key={pubType}>
                  {pubType}
                </option>
              ))}
            </select>
            {publicationType === "Other" && (
              <div className="my-3">
                <input
                  type="text"
                  className="form-control mt-2"
                  name="otherPublicationType"
                  placeHolder={t("common.placeHolder.otherPublicationType")}
                  value={otherPublicationType}
                  onChange={handleChange}
                />
              </div>
            )}
            {errMsg.publicationType && (
              <p className="error">{errMsg.publicationType}</p>
            )}
          </div>
          <div className="col-md-12 mb-3">
            <label className="form-label">
              {t("common.label.journalOrConference")}
            </label>
            <input
              type="text"
              className="form-control"
              name="journalOrConference"
              placeHolder={t("common.placeHolder.journalOrConference")}
              value={journalOrConference}
              onChange={handleChange}
            />
            {errMsg.journalOrConference && (
              <p className="error">{errMsg.journalOrConference}</p>
            )}
          </div>
          <div className="col-md-12 mb-3">
            <label className="form-label">{t("common.label.authors")}</label>
            {authors.map((author, idx) => (
              <div key={idx} className="d-flex mb-2">
                <input
                  type="text"
                  className="form-control me-2"
                  value={author}
                  onChange={(e) => handleAuthorChange(idx, e.target.value)}
                  placeHolder={`${t("common.label.authors")} ${idx + 1}`}
                />
                <div className="d-inline-flex align-items-center gap-1">
                  {authors.length > 1 && (
                    <Button
                      variant="outline-danger-light"
                      size="sm"
                      onClick={() => handleAuthorRemove(idx)}
                    >
                      <i className="bx bx-trash"></i>
                    </Button>
                  )}
                  {idx === authors.length - 1 && (
                    <Button
                      variant="outline-primary-light"
                      size="sm"
                      onClick={handleAuthorAdd}
                    >
                      <i className="bx bx-plus"></i>
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {errMsg.authors && <p className="error">{errMsg.authors}</p>}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.isFirstAuthor")}{" "}
              <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              name="isFirstAuthor"
              value={isFirstAuthor}
              onChange={handleChange}
            >
              <option value="">{t("common.placeHolder.isFirstAuthor")}</option>
              <option value="yes">{t("common.label.yes")}</option>
              <option value="no">{t("common.label.no")}</option>
            </select>
            {errMsg.isFirstAuthor && (
              <p className="error">{errMsg.isFirstAuthor}</p>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.yearOfPublication")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              className="form-control"
              name="yearOfPublication"
              placeHolder={t("common.placeHolder.yearOfPublication")}
              value={yearOfPublication}
              onChange={handleChange}
            />
            {errMsg.yearOfPublication && (
              <p className="error">{errMsg.yearOfPublication}</p>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">{t("common.label.doiOrUlr")}</label>
            <input
              type="text"
              className="form-control"
              name="doiOrUlr"
              placeHolder={t("common.placeHolder.doiOrUlr")}
              value={doiOrUlr}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.publisherName")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="publisherName"
              placeHolder={t("common.placeHolder.publisherName")}
              value={publisherName}
              onChange={handleChange}
            />
            {errMsg.publisherName && (
              <p className="error">{errMsg.publisherName}</p>
            )}
          </div>
          <div className="col-md-12 mb-3">
            <label className="form-label">{t("common.label.abstract")}</label>
            <textarea
              className="form-control"
              name="abstract"
              placeHolder={t("common.placeHolder.abstract")}
              value={abstract}
              onChange={handleChange}
              rows={4}
            />
          </div>
          <div className="col-md-12 mb-3">
            <label className="form-label">
              {t("common.label.keywordsOrResearchTopics")}
            </label>
            <input
              type="text"
              className="form-control"
              name="keywordsOrResearcTopics"
              placeHolder={t("common.placeHolder.keywordsOrResearchTopics")}
              value={keywordsOrResearcTopics}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.publicationOpenAccess")}{" "}
              <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              name="publicationOpenAccess"
              value={publicationOpenAccess}
              onChange={handleChange}
            >
              <option value="">
                {t("common.placeHolder.publicationOpenAccess")}
              </option>
              <option value="yes">{t("common.label.yes")}</option>
              <option value="no">{t("common.label.no")}</option>
            </select>
            {errMsg.publicationOpenAccess && (
              <p className="error">{errMsg.publicationOpenAccess}</p>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">{t("common.label.uploadPdf")}</label>
            {uploadPdf ? (
              <div className="">
                <div className="form-control py-0-5 pr-0-5 d-flex align-items-center justify-content-between">
                  <span className="">
                    {uploadPdf.name} ({uploadPdf.size})
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger-light ms-2 w-7 h-7 p-0 d-flex justify-content-center align-items-center"
                    onClick={handleFileRemove}
                  >
                    <i className="bx bx-trash"></i>
                  </button>
                </div>
              </div>
            ) : (
              <input
                type="file"
                className="form-control"
                name="uploadPdf"
                accept="application/uploadPdf"
                onChange={handleFileChange}
              />
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={toggle}>
          {t("common.button.cancel")}
        </Button>
        {loading ? (
          <ButtonLoader />
        ) : (
          <Button
            variant="primary"
            onClick={onSavePublication}
            disabled={uploadLoading}
          >
            {t("common.button.save")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
